import { Component, inject, Input, ChangeDetectionStrategy, OnChanges, SimpleChanges } from '@angular/core'
import { AsyncPipe, DatePipe } from '@angular/common'
import { MatDialog } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { ProgressLoaderComponent } from '../common/progress-loader/progress-loader.component'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import {
  Meeting,
  MeetingProcessingStatus,
  MeetingsService,
  PendingRetentionExtensionRequest,
  RetentionExtensionRequestOptions,
  computeIsCheckingAgendaValidity,
} from '../meetings.service'
import { DurationPipe } from '../duration.pipe'
import { MeetingHealthIndicatorComponent } from '../meeting-health-indicator/meeting-health-indicator.component'
import { getMeetingHealthItems } from '../meeting-health-indicator/meeting-health.utils'
import { TemplateItem } from '../template-editor.component/types/template.types'
import {
  RequestRetentionExtensionDialogComponent,
  RequestRetentionExtensionDialogData,
} from '../request-retention-extension-dialog/request-retention-extension-dialog.component'

@Component({
  selector: 'app-meeting-details-bar',
  standalone: true,
  imports: [AsyncPipe, DatePipe, MatIcon, ProgressLoaderComponent, MatTooltip, TranslatePipe, DurationPipe, MeetingHealthIndicatorComponent],
  templateUrl: './meeting-details-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-details-bar.component.scss',
})
export class MeetingDetailsBarComponent implements OnChanges {
  private translateService = inject(TranslateService)
  private meetingsService = inject(MeetingsService)
  private dialog = inject(MatDialog)

  @Input({ required: true })
  meeting!: Meeting

  @Input({ required: true })
  templateItems!: TemplateItem[]

  retentionExtensionRequestOptions$: ReturnType<MeetingsService['getRetentionExtensionRequestOptions']> | null = null

  ngOnChanges(changes: SimpleChanges) {
    const previousMeeting: Meeting | undefined = changes['meeting']?.previousValue
    if (changes['meeting'] !== undefined && (previousMeeting?.id ?? null) !== this.meeting.id) {
      this.retentionExtensionRequestOptions$ = this.meetingsService.getRetentionExtensionRequestOptions(this.meeting.id)
    }
  }

  openRequestRetentionExtensionDialog(options: RetentionExtensionRequestOptions) {
    if (this.meeting.retentionPeriodExpirationDate === null) {
      return
    }
    const data: RequestRetentionExtensionDialogData = {
      meetingId: this.meeting.id,
      meetingName: this.meeting.name,
      meetingDate: new Date(this.meeting.date),
      currentSoftDeletionDate: new Date(this.meeting.retentionPeriodExpirationDate),
      availableExtensionsInDays: options.availableExtensionsInDays,
    }
    this.dialog.open(RequestRetentionExtensionDialogComponent, { data, autoFocus: false })
  }

  getPendingRequestTooltip(pendingRequest: PendingRetentionExtensionRequest): string {
    const daysKey = pendingRequest.extensionInDays === 1 ? 'MEETINGS.RETENTION_EXTENSION_REQUEST.DAY' : 'MEETINGS.RETENTION_EXTENSION_REQUEST.DAYS'
    return this.translateService.instant('MEETINGS.RETENTION_EXTENSION_REQUEST.PENDING_TOOLTIP', {
      requestedByName: pendingRequest.requestedByName,
      days: this.translateService.instant(daysKey, { count: pendingRequest.extensionInDays }),
    })
  }

  getRequestableExpiryTooltip(): string {
    return `${this.getExpiryTooltip()} ${this.translateService.instant('MEETINGS.RETENTION_EXTENSION_REQUEST.BADGE_HINT')}`
  }

  get isSplitting(): boolean {
    return (
      this.meeting.totalNumberOfSplits > 0 &&
      (this.meeting.processingStatus === MeetingProcessingStatus.SPLITTING_MEETING ||
        this.meeting.processingStatus === MeetingProcessingStatus.PROCESSING_MEDIA)
    )
  }

  get isCheckingAgendaValidity(): boolean {
    return computeIsCheckingAgendaValidity(this.meeting)
  }

  getMeetingHealthItems() {
    return getMeetingHealthItems(this.meeting, this.templateItems, (key: string, params?: Record<string, unknown>) =>
      this.translateService.instant(key, params)
    )
  }

  // TODO Fix the typing of these methods after correctly transforming dates in the server response on meetings.service.ts
  getExpiryText(): string {
    if (this.meeting.retentionPeriodExpirationDate === null) {
      return this.translateService.instant('MEETINGS.EXPIRES.NEVER')
    }

    const now = new Date()
    const hoursLeft = Math.max((new Date(this.meeting.retentionPeriodExpirationDate).getTime() - now.getTime()) / (1000 * 60 * 60), 0)

    if (hoursLeft >= 24) {
      const days = Math.round(hoursLeft / 24)
      const key = days === 1 ? 'MEETINGS.EXPIRES.DAYS' : 'MEETINGS.EXPIRES.DAYS_PLURAL'
      return this.translateService.instant(key, { count: days })
    } else if (hoursLeft >= 1.5) {
      const hours = Math.round(hoursLeft)
      const key = hours === 1 ? 'MEETINGS.EXPIRES.HOURS' : 'MEETINGS.EXPIRES.HOURS_PLURAL'
      return this.translateService.instant(key, { count: hours })
    } else {
      const minutes = Math.round(hoursLeft * 60)
      const key = minutes === 1 ? 'MEETINGS.EXPIRES.MINUTES' : 'MEETINGS.EXPIRES.MINUTES_PLURAL'
      return this.translateService.instant(key, { count: minutes })
    }
  }

  get isExpiryUrgent(): boolean {
    if (this.meeting.retentionPeriodExpirationDate === null) {
      return false
    }

    const now = new Date()
    const hoursLeft = (new Date(this.meeting.retentionPeriodExpirationDate).getTime() - now.getTime()) / (1000 * 60 * 60)
    return hoursLeft < 24
  }

  getExpiryTooltip(): string {
    if (this.meeting.retentionPeriodExpirationDate === null) {
      return this.translateService.instant('MEETINGS.EXPIRES.TOOLTIP_NEVER')
    }

    const expiryDate = new Date(this.meeting.retentionPeriodExpirationDate)
    const day = expiryDate.getDate()
    const monthNames = [
      this.translateService.instant('COMMON.MONTHS_SHORT.JAN'),
      this.translateService.instant('COMMON.MONTHS_SHORT.FEB'),
      this.translateService.instant('COMMON.MONTHS_SHORT.MAR'),
      this.translateService.instant('COMMON.MONTHS_SHORT.APR'),
      this.translateService.instant('COMMON.MONTHS_SHORT.MAY'),
      this.translateService.instant('COMMON.MONTHS_SHORT.JUN'),
      this.translateService.instant('COMMON.MONTHS_SHORT.JUL'),
      this.translateService.instant('COMMON.MONTHS_SHORT.AUG'),
      this.translateService.instant('COMMON.MONTHS_SHORT.SEP'),
      this.translateService.instant('COMMON.MONTHS_SHORT.OCT'),
      this.translateService.instant('COMMON.MONTHS_SHORT.NOV'),
      this.translateService.instant('COMMON.MONTHS_SHORT.DEC'),
    ]
    const month = monthNames[expiryDate.getMonth()]
    const year = expiryDate.getFullYear()
    const hours = expiryDate.getHours().toString().padStart(2, '0')
    const minutes = expiryDate.getMinutes().toString().padStart(2, '0')

    return this.translateService.instant('MEETINGS.EXPIRES.TOOLTIP_DATE', {
      day: day,
      month: month,
      year: year,
      hours: hours,
      minutes: minutes,
    })
  }
}
